import React, { Suspense } from "react";
import { type IconProps } from "@mui/material/Icon";
import { type SvgIconTypeMap } from "@mui/material/SvgIcon";
import { type OverridableComponent } from "@mui/material/OverridableComponent";
import CircularProgress from "@mui/material/CircularProgress";

import ArrowBack from "@mui/icons-material/ArrowBack";
import ArrowBackIos from "@mui/icons-material/ArrowBackIos";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import AssignmentOutlined from "@mui/icons-material/AssignmentOutlined";
import BusinessRounded from "@mui/icons-material/BusinessRounded";
import ClearRounded from "@mui/icons-material/ClearRounded";
import Delete from "@mui/icons-material/Delete";
import Description from "@mui/icons-material/Description";
import DoneRounded from "@mui/icons-material/DoneRounded";
import Download from "@mui/icons-material/Download";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import ErrorRounded from "@mui/icons-material/ErrorRounded";
import EventRounded from "@mui/icons-material/EventRounded";
import ExpandMore from "@mui/icons-material/ExpandMore";
import HelpOutlineRounded from "@mui/icons-material/HelpOutlineRounded";
import HighlightOffRounded from "@mui/icons-material/HighlightOffRounded";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import LocalHospital from "@mui/icons-material/LocalHospital";
import LockOutlined from "@mui/icons-material/LockOutlined";
import MoreVert from "@mui/icons-material/MoreVert";
import NavigateBeforeRounded from "@mui/icons-material/NavigateBeforeRounded";
import NavigateNextRounded from "@mui/icons-material/NavigateNextRounded";
import NoteAlt from "@mui/icons-material/NoteAlt";
import Person from "@mui/icons-material/Person";
import PrintOutlined from "@mui/icons-material/PrintOutlined";
import Refresh from "@mui/icons-material/Refresh";
import SaveAltRounded from "@mui/icons-material/SaveAltRounded";
import UploadFile from "@mui/icons-material/UploadFile";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import WarningRounded from "@mui/icons-material/WarningRounded";

type MuiIconType = OverridableComponent<SvgIconTypeMap<{}, "svg">> & { muiName: string; }

// in order to properly tree shake and only import the icons we need, we maintain a mapping of icon names to their respective components.
const MuiIcons: Record<string, MuiIconType> = {
    ArrowBack,
    ArrowBackIos,
    ArrowDownward,
    ArrowForwardIos,
    ArrowForwardRounded,
    AssignmentOutlined,
    BusinessRounded,
    ClearRounded,
    Delete,
    Description,
    DoneRounded,
    Download,
    EditOutlined,
    ErrorOutlineRounded,
    ErrorRounded,
    EventRounded,
    ExpandMore,
    HelpOutlineRounded,
    HighlightOffRounded,
    InfoOutlined,
    KeyboardArrowDown,
    KeyboardArrowDownRounded,
    LocalHospital,
    LockOutlined,
    MoreVert,
    NavigateBeforeRounded,
    NavigateNextRounded,
    NoteAlt,
    Person,
    PrintOutlined,
    Refresh,
    SaveAltRounded,
    UploadFile,
    Visibility,
    VisibilityOff,
    WarningAmberOutlined,
    WarningRounded,
};

import "./IconPane.scss";

export interface IconPanePropsWithName extends IconProps {
    iconName: string;
    icon?: never;
}

export interface IconPanePropsWithIcon extends IconProps {
    icon: React.ReactElement;
    iconName?: never;
}

export type IconPaneProps = IconPanePropsWithName | IconPanePropsWithIcon;

export const IconPane: React.FC<IconPaneProps> = React.memo(({ iconName, icon, ...props}) => {
    let IconComponent: MuiIconType | null = null;
    if (iconName) {
        IconComponent = MuiIcons[iconName] || ((() => <span>{iconName}</span>) as unknown as MuiIconType);
    }

    return (
        <div className="icon-pane">
            <Suspense fallback={<CircularProgress size="2rem" />}>
                {/* @ts-expect-error */}
                {iconName && IconComponent && <IconComponent {...props}  />}
                {icon}
            </Suspense>
        </div>
    );
});
